import { type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchUserAuthEvents,
  fetchUserSessions,
  revokeUserSession,
  type AuthEventItem,
  type SessionItem,
} from '../../api/admin.js';
import { formatDateTime } from '../../lib/format.js';

/** Tono de badge por tipo de evento de auth. */
function eventTone(eventType: string): string {
  if (eventType === 'signin_failed') return 'danger';
  if (eventType === 'signin' || eventType === 'signup') return 'info';
  if (eventType === 'password_reset') return 'warn';
  return 'neutral';
}

/** Resumen legible del dispositivo a partir del UA parseado. */
function deviceSummary(e: AuthEventItem | SessionItem): string {
  if ('browser' in e) {
    const parts = [e.browser, e.os, e.deviceType].filter(Boolean);
    if (parts.length > 0) return parts.join(' · ');
  }
  return e.userAgent ? e.userAgent.slice(0, 48) : '—';
}

function geoSummary(e: AuthEventItem): string {
  const parts = [e.city, e.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

export function SecurityTab({ userId }: { userId: string }): ReactElement {
  return (
    <>
      <ActiveSessions userId={userId} />
      <AuthEventsLog userId={userId} />
    </>
  );
}

function ActiveSessions({ userId }: { userId: string }): ReactElement {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin', 'user', userId, 'sessions'],
    queryFn: () => fetchUserSessions(userId),
  });
  const revokeMut = useMutation({
    mutationFn: (target: { sessionId: string } | { all: true }) =>
      revokeUserSession(userId, target),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'user', userId, 'sessions'] });
    },
  });

  const activeCount = q.data?.items.filter((s) => s.status === 'active').length ?? 0;

  return (
    <section className="card" style={{ marginBottom: 24 }}>
      <header className="card-head" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h3 className="card-title" style={{ flex: 1 }}>
          Sesiones activas <small>{activeCount}</small>
        </h3>
        {activeCount > 0 ? (
          <button
            type="button"
            className="btn btn-danger"
            disabled={revokeMut.isPending}
            onClick={() => {
              if (confirm('Revocar TODAS las sesiones de este user? Tendrá que volver a loguearse.')) {
                revokeMut.mutate({ all: true });
              }
            }}
            data-testid="revoke-all-sessions"
          >
            Revocar todas
          </button>
        ) : null}
      </header>
      {q.isPending ? (
        <div className="loading">Cargando…</div>
      ) : q.isError ? (
        <div className="loading">Error: {q.error.message}</div>
      ) : q.data.items.length === 0 ? (
        <div className="empty">Sin sesiones registradas</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Iniciada</th>
              <th>IP</th>
              <th>Dispositivo</th>
              <th>Expira</th>
              <th>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((s) => (
              <tr key={s.id}>
                <td className="tt-tert mono">{formatDateTime(s.createdAt)}</td>
                <td className="mono">{s.ipAddress ?? '—'}</td>
                <td className="tt-sec">{deviceSummary(s)}</td>
                <td className="tt-tert mono">{formatDateTime(s.expiresAt)}</td>
                <td>
                  <span className={`badge b-${statusTone(s.status)}`}>{s.status}</span>
                </td>
                <td className="r">
                  {s.status === 'active' ? (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={revokeMut.isPending}
                      onClick={() => {
                        if (confirm('Revocar esta sesión?')) revokeMut.mutate({ sessionId: s.id });
                      }}
                      data-testid="revoke-session"
                    >
                      Revocar
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {revokeMut.isError ? (
        <p className="error" style={{ padding: 12 }}>
          {revokeMut.error instanceof Error ? revokeMut.error.message : 'Error al revocar'}
        </p>
      ) : null}
    </section>
  );
}

function statusTone(status: SessionItem['status']): string {
  if (status === 'active') return 'info';
  if (status === 'revoked') return 'warn';
  return 'neutral';
}

function AuthEventsLog({ userId }: { userId: string }): ReactElement {
  const q = useQuery({
    queryKey: ['admin', 'user', userId, 'auth-events'],
    queryFn: () => fetchUserAuthEvents(userId, { limit: 50 }),
  });
  return (
    <section className="card">
      <header className="card-head">
        <h3 className="card-title">
          Historial de accesos <small>últimos 50</small>
        </h3>
      </header>
      {q.isPending ? (
        <div className="loading">Cargando…</div>
      ) : q.isError ? (
        <div className="loading">Error: {q.error.message}</div>
      ) : q.data.items.length === 0 ? (
        <div className="empty">Sin eventos de acceso</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Cuándo</th>
              <th>Evento</th>
              <th>App</th>
              <th>IP</th>
              <th>Ubicación</th>
              <th>Dispositivo</th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((e) => (
              <tr key={e.id}>
                <td className="tt-tert mono">{formatDateTime(e.createdAt)}</td>
                <td>
                  <span className={`badge b-${eventTone(e.eventType)}`}>{e.eventType}</span>
                  {e.failureReason ? (
                    <span className="tt-tert" style={{ marginLeft: 6 }}>
                      {e.failureReason}
                    </span>
                  ) : null}
                </td>
                <td className="tt-sec">{e.appSource ?? '—'}</td>
                <td className="mono">{e.ipAddress ?? '—'}</td>
                <td className="tt-sec">{geoSummary(e)}</td>
                <td className="tt-sec">{deviceSummary(e)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
